import { ArrowRight, ShoppingBag } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS, HERBALIFE_STORE_URL } from "@/constants/products";

const Shop = () => (
  <main data-testid="shop-page" className="pt-28">
    <section className="mx-auto max-w-7xl px-6 pb-14 pt-10 text-center">
      <h1 data-testid="shop-headline" className="font-display text-4xl font-black uppercase tracking-tighter text-ink sm:text-5xl md:text-6xl">
        <MaskedLine delay={0.15}>Shop my favorite</MaskedLine>
        <MaskedLine delay={0.3}>
          <span className="text-coral">Herbalife products ❤️</span>
        </MaskedLine>
      </h1>
      <FadeUp delay={0.5}>
        <p data-testid="shop-subtitle" className="mx-auto mt-6 max-w-xl font-body text-base font-light leading-relaxed text-ink/70 md:text-lg">
          These are some of the products I personally love and use. Click any product to shop directly.
        </p>
      </FadeUp>
    </section>

    <section className="mx-auto max-w-7xl px-6 pb-24">
      <FadeUp>
        <div
          data-testid="miracle-pack-card"
          className="mb-14 grid items-center gap-8 overflow-hidden rounded-3xl border border-coral/30 bg-white shadow-lg shadow-coral/10 md:grid-cols-2"
        >
          <div className="relative">
            <img
              src="/miracle-pack.jpeg"
              alt="Shelly's miracle pack — Cell-U-Loss, Aminogen, Thermo-Bond, Prolessa Duo and Herbal Tea Concentrate"
              className="h-full w-full object-cover"
            />
            <span className="absolute right-3 top-3 rounded-full bg-coral/95 px-2.5 py-1 font-display text-[10px] font-bold tracking-[0.12em] text-bone shadow">
              ❤️ FAV
            </span>
          </div>
          <div className="p-8 md:p-10">
            <p className="font-display text-xs font-bold tracking-[0.3em] text-coral">MIRACLE PACK</p>
            <h2 className="mt-3 font-display text-3xl font-black uppercase tracking-tight text-ink md:text-4xl">
              Can't live without this <span className="text-coral">❤️ miracle pack</span>
            </h2>
            <p className="mt-4 font-body text-base font-light leading-relaxed text-ink/70">
              My everyday power combo — Cell-U-Loss, Aminogen, Prolessa Duo and my Herbal Tea. Grab your favorites from the pack:
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://shellyperreault.herbalife.com/en-us/u/products/aminogen-60-tablets-0101"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pack-aminogen-button"
                className="rounded-full bg-forest px-6 py-3.5 font-display text-xs font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coral"
              >
                SHOP AMINOGEN
              </a>
              <a
                href="https://shellyperreault.herbalife.com/en-us/u/products/cell-u-loss-90-tablets-0111"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pack-celluloss-button"
                className="rounded-full bg-forest px-6 py-3.5 font-display text-xs font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coral"
              >
                SHOP CELL-U-LOSS
              </a>
              <a
                href="https://shellyperreault.herbalife.com/en-us/u/search?q=Prolessa&tab=Products"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pack-prolessa-button"
                className="rounded-full bg-forest px-6 py-3.5 font-display text-xs font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coral"
              >
                SHOP PROLESSA
              </a>
              <a
                href="https://shellyperreault.herbalife.com/en-us/u/search?q=TeAs&tab=Products"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pack-tea-button"
                className="rounded-full bg-forest px-6 py-3.5 font-display text-xs font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coral"
              >
                SHOP TEA
              </a>
              <a
                href="https://shellyperreault.herbalife.com/en-us/u/products/fat-release-fruit-twist-30-stick-packs-196k"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pack-fat-release-button"
                className="rounded-full bg-coral px-6 py-3.5 font-display text-xs font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coraldark"
              >
                SHOP FAT RELEASE ✨
              </a>
            </div>
            <p data-testid="pack-replacement-note" className="mt-5 rounded-2xl border border-coral/25 bg-coral/5 p-4 font-body text-sm font-light leading-relaxed text-ink/70">
              Heads up: <span className="font-semibold text-ink">Thermo-Bond is now being replaced by Fat Release</span> (Fruit Twist, 30 stick packs) — grab the new one above. ✨
            </p>
          </div>
        </div>
      </FadeUp>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {PRODUCTS.map((p, i) => (
          <ProductCard key={p.id} product={p} delay={0.06 * i} />
        ))}
      </div>
    </section>

    <section className="grain relative overflow-hidden bg-forest px-6 py-24 text-center md:py-28">
      <FadeUp>
        <ShoppingBag className="mx-auto h-10 w-10 text-coral" strokeWidth={1.5} />
        <h2 data-testid="shop-everything-headline" className="mt-6 font-display text-4xl font-black uppercase tracking-tight text-bone md:text-5xl">
          Want to see everything?
        </h2>
        <p className="mx-auto mt-4 max-w-md font-body text-base font-light leading-relaxed text-bone/75 md:text-lg">
          Browse my full Herbalife shopping website.
        </p>
        <a
          href={HERBALIFE_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="shop-all-button"
          className="mt-10 inline-flex items-center gap-2 rounded-full bg-coral px-10 py-5 font-display text-sm font-bold tracking-[0.15em] text-bone transition-all duration-300 hover:scale-105 hover:bg-coraldark"
        >
          SHOP ALL HERBALIFE PRODUCTS
          <ArrowRight className="h-4 w-4" />
        </a>
      </FadeUp>
    </section>
  </main>
);

export default Shop;
